import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import Topbar from "@/components/Topbar";
import {
  Plus, PencilSimple, Trash, FilePdf, X, Paperclip, MagnifyingGlass,
  CaretRight, CaretLeft, Car, DownloadSimple, UploadSimple, Funnel,
  ChatCircleDots, CalendarPlus, PaperPlaneRight, Sparkle, UsersFour,
  TrendUp, ClockCounterClockwise, Star, Lightning, ArrowsDownUp, Storefront,
  InstagramLogo, FacebookLogo, GoogleLogo, Megaphone,
  Files, CheckCircle, ArrowClockwise, Image as ImageIcon, FileText, Eye,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import Combobox from "@/components/ui/Combobox";
import { CAR_BRANDS, VEHICLE_COLORS, modelsForBrand } from "@/lib/vehicleCatalog";
import { useSector } from "@/lib/sectorContext";
import CustomerBrainPanel from "@/components/customer/CustomerBrainPanel";
import ClinicalNotesPanel from "@/components/customer/ClinicalNotesPanel";

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 350;

const emptyCustomer = {
  name: "", phone: "", email: "",
  document: "", document_type: "cpf",
  document_file_url: "",
  address: "",
  tier: "regular",
  birthday: "",
};

const emptyVehicle = {
  identifier: "", brand: "", model: "", year: "", color: "", km: "",
  // v0.98 · histórico de manutenção (mesmos campos do importador)
  last_oil_change_at: "", last_oil_change_km: "",
  last_service_at: "", next_service_at: "", next_oil_change_at: "",
};

const DOC_TYPES = [
  { value: "cpf", label: "CPF" },
  { value: "cnpj", label: "CNPJ" },
  { value: "rg", label: "RG" },
  { value: "cnh", label: "CNH" },
  { value: "passport", label: "Passaporte" },
  { value: "other", label: "Outro" },
];

const CHIPS = [
  { code: "",                label: "Todos",            icon: UsersFour },
  { code: "active",          label: "Ativos",           icon: Lightning },
  { code: "new",             label: "Novos",            icon: Sparkle },
  { code: "inactive",        label: "Inativos",         icon: ClockCounterClockwise },
  { code: "opportunity",     label: "Oportunidades",    icon: TrendUp },
  { code: "no_vehicle",      label: "Sem veículo",      icon: Car },
  { code: "with_appointment",label: "Com agendamento",  icon: CalendarPlus },
  { code: "service_soon",    label: "Troca próxima",    icon: Lightning },
];

const SORT_OPTIONS = [
  { value: "recent",     label: "Mais recentes" },
  { value: "oldest",     label: "Mais antigos" },
  { value: "name_asc",   label: "Nome A–Z" },
  { value: "name_desc",  label: "Nome Z–A" },
];

const SOURCE_META = {
  instagram_ad: { label: "Instagram (anúncio)", icon: InstagramLogo, cls: "bg-violet-50 text-violet-700 border-violet-200" },
  facebook_ad:  { label: "Facebook (anúncio)",  icon: FacebookLogo,  cls: "bg-blue-50 text-blue-700 border-blue-200" },
  ad_generic:   { label: "Anúncio",             icon: Megaphone,     cls: "bg-violet-50 text-violet-700 border-violet-200" },
  instagram:    { label: "Instagram",           icon: InstagramLogo, cls: "bg-pink-50 text-pink-700 border-pink-200" },
  facebook:     { label: "Facebook",            icon: FacebookLogo,  cls: "bg-blue-50 text-blue-700 border-blue-200" },
  google:       { label: "Google",              icon: GoogleLogo,    cls: "bg-amber-50 text-amber-700 border-amber-200" },
  import:       { label: "Importação",          icon: UploadSimple,  cls: "bg-slate-100 text-slate-700 border-slate-200" },
  whatsapp:     { label: "WhatsApp",            icon: ChatCircleDots, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  manual:       { label: "Manual",              icon: Storefront,    cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return iso.slice(0, 10); }
}

function relativeTime(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diffMin < 1) return "agora";
    if (diffMin < 60) return `${diffMin}min atrás`;
    const h = Math.floor(diffMin / 60);
    if (h < 24) return `${h}h atrás`;
    const days = Math.floor(h / 24);
    if (days < 30) return `${days}d atrás`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mês${months > 1 ? "es" : ""} atrás`;
    return `${Math.floor(months / 12)}a atrás`;
  } catch { return null; }
}

function OpportunityBadge({ tag, enablesAssetTab = true, assetSingular = "veículo" }) {
  if (!tag) return null;
  // v0.99.49 — sanitiza tag quando o backend ainda devolve "Sem veículo"
  // pra setores sem ativo (Igreja/Mandato). Nesses casos a tag perde
  // sentido e deve sumir ao invés de confundir o operador.
  if (!enablesAssetTab && /sem\s+ve[íi]culo/i.test(tag)) return null;
  // Troca "veículo" pela terminologia do setor quando aplicável.
  const displayTag = enablesAssetTab && assetSingular && assetSingular !== "veículo"
    ? tag.replace(/ve[íi]culo/gi, assetSingular)
    : tag;
  // Apple-style: monochrome. Differentiation by weight, not hue.
  let cls = "bg-slate-100 text-slate-700 border-slate-200/70";
  if (tag.includes("Parado"))        cls = "bg-slate-900 text-white border-slate-900";
  else if (tag.includes("Troca"))    cls = "bg-slate-200 text-slate-900 border-slate-300";
  else if (tag.includes("Agend"))    cls = "bg-slate-100 text-slate-900 border-slate-200 font-semibold";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-medium border max-w-full truncate ${cls}`}
      data-testid="customer-opportunity"
      title={displayTag}
    >
      {displayTag}
    </span>
  );
}

function SourceBadge({ source }) {
  const meta = SOURCE_META[source];
  if (!meta) return null;
  const Icon = meta.icon;
  // Apple: a single neutral pill, the icon carries the source identity.
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border bg-slate-50 text-slate-600 border-slate-200/80 max-w-[120px] truncate"
      title={meta.label}
    >
      <Icon size={10} weight="fill" className="text-slate-500" />
      <span className="truncate">{meta.label}</span>
    </span>
  );
}

function VipBadge() {
  // Apple-mono: a tiny inverted pill. The badge is intentionally subtle —
  // it carries weight by contrast (white-on-black), not by color.
  return (
    <span
      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9.5px] uppercase tracking-[0.16em] font-bold bg-slate-900 text-white"
      title="Cliente VIP — recebe áudio humanizado, ofertas exclusivas e prioridade nas automações"
      data-testid="customer-vip-badge"
    >
      VIP
    </span>
  );
}

function StatCard({ icon: Icon, label, value, onClick, active, testid }) {
  // Apple-style: a single neutral surface, slate hierarchy.
  // Active state = darker ring (no color flash). Hover = subtle elevation.
  const interactive = typeof onClick === "function";
  const activeCls = active
    ? "ring-2 ring-slate-900 shadow-md shadow-slate-900/10"
    : `ring-1 ring-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${interactive ? "hover:shadow-md hover:shadow-slate-900/5 hover:-translate-y-[1px]" : ""}`;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      disabled={!interactive}
      className={`group text-left rounded-2xl bg-white ${activeCls} px-3.5 py-3 transition-all duration-200 ease-out disabled:cursor-default min-w-0`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="w-7 h-7 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
          <Icon size={13} weight="fill" />
        </span>
        <span className="text-[10.5px] uppercase tracking-[0.12em] text-slate-500 font-semibold truncate" title={label}>
          {label}
        </span>
      </div>
      <div className="text-[26px] leading-none font-semibold text-slate-900 tabular-nums mt-2 truncate">
        {value === null || value === undefined ? "—" : Number(value).toLocaleString("pt-BR")}
      </div>
    </button>
  );
}

// ─── Premium iOS-style modal shell ──────────────────────────────────────
// Reusable wrapper used by all dialogs on this page so we get one source of
// truth for spacing, blur, shadow and overflow handling. Title is forced to
// `truncate` so a long subtitle never pushes the close button off-screen.
function PremiumModal({ icon: Icon, title, subtitle, onClose, children, footer, size = "md", testid }) {
  const SIZES = { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-2xl", xl: "max-w-3xl" };
  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-4 bg-slate-950/55 backdrop-blur-md animate-in fade-in duration-150"
      onClick={onClose}
      data-testid={testid}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative bg-white w-full ${SIZES[size]} max-h-[92vh] flex flex-col rounded-t-2xl md:rounded-2xl shadow-2xl shadow-slate-900/30 ring-1 ring-slate-200/60 overflow-hidden animate-in slide-in-from-bottom-4 md:zoom-in-95 duration-200`}
      >
        {/* Header — Apple monochrome */}
        <header className="flex items-start gap-3 px-5 md:px-6 pt-5 md:pt-6 pb-4 border-b border-slate-100">
          {Icon && (
            <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-slate-100 text-slate-700">
              <Icon size={18} weight="fill" />
            </span>
          )}
          <div className="min-w-0 flex-1">
            <h3 className="font-heading text-[17px] font-semibold tracking-tight text-slate-900 truncate" title={title}>
              {title}
            </h3>
            {subtitle && (
              <p className="text-[12.5px] text-slate-500 mt-0.5 line-clamp-2 leading-snug">
                {subtitle}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="shrink-0 -mt-1 -mr-1 w-8 h-8 rounded-full text-slate-400 hover:text-slate-900 hover:bg-slate-100 flex items-center justify-center transition-colors"
            data-testid="modal-close"
          >
            <X size={16} weight="bold" />
          </button>
        </header>
        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 md:px-6 py-5 space-y-4">
          {children}
        </div>
        {/* Footer */}
        {footer && (
          <footer className="bg-slate-50/70 border-t border-slate-100 px-5 md:px-6 py-3.5 flex items-center justify-end gap-2">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

// Field helper for premium inputs (rounded-xl, taller, smoother focus)
function FieldLabel({ children }) {
  return (
    <span className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-slate-500">
      {children}
    </span>
  );
}
const inputCls = "mt-1.5 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-[13.5px] bg-white focus:outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 transition-all";
const inputClsMono = inputCls + " font-mono";

export default function Customers() {
  const [page, setPage] = useState({ items: [], total: 0, has_more: false, offset: 0 });
  const [stats, setStats] = useState({ total: null, new_30d: null, inactive: null, opportunities: null, with_vehicle: null });
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [chip, setChip] = useState("");
  const [sort, setSort] = useState("recent");
  const [filterOpen, setFilterOpen] = useState(false);
  const [extraFilters, setExtraFilters] = useState({ brand: "", model: "", year_from: "", year_to: "" });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyCustomer);
  const [vehicleForm, setVehicleForm] = useState(emptyVehicle);
  const [includeVehicleOnCreate, setIncludeVehicleOnCreate] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [vehicleDrawerCustomer, setVehicleDrawerCustomer] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [selectAllAcrossPages, setSelectAllAcrossPages] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const nav = useNavigate();
  const { labels, isClinic, isAutomotive, enablesAssetTab, enablesKm } = useSector();
  const assetSingular = labels.assetSingular();      // "veículo" | "paciente" | "pet" | "cliente" | ...
  const assetSingularCap = labels.assetSingular(true);
  const assetPluralCap = labels.assetPlural(true);   // "Veículos" | "Pacientes" | ...
  // v0.96.24 — heurística universal:
  // • enablesAssetTab=true (automotive/petshop): mostra "Clientes e {plural}"
  // • false (clinic/salon/gym/etc): só "{plural}" (o cliente É o alvo)
  const customersPageTitle = enablesAssetTab
    ? `Clientes e ${assetPluralCap}`
    : assetPluralCap;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to first page when any filter changes
  useEffect(() => { setOffset(0); }, [debouncedSearch, chip, sort, extraFilters]);

  useEffect(() => {
    setSelected(new Set());
    setSelectAllAcrossPages(false);
  }, [debouncedSearch, chip]);

  // Topbar global search deep-link
  useEffect(() => {
    const focus = searchParams.get("focus");
    if (!focus) return;
    setSearch(focus);
    const next = new URLSearchParams(searchParams);
    next.delete("focus");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        search: debouncedSearch,
        chip, sort,
        limit: PAGE_SIZE,
        offset,
        with_vehicles: true,
        ...(extraFilters.brand && { brand: extraFilters.brand }),
        ...(extraFilters.model && { model: extraFilters.model }),
        ...(extraFilters.year_from && { year_from: parseInt(extraFilters.year_from, 10) }),
        ...(extraFilters.year_to && { year_to: parseInt(extraFilters.year_to, 10) }),
      };
      const { data } = await api.get("/customers/page", { params });
      setPage(data);
    } catch {
      toast.error("Não consegui carregar os clientes.");
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data } = await api.get("/customers/stats");
      setStats(data);
    } catch { /* silent — counters are non-blocking */ }
  };

  useEffect(() => { load(); }, [debouncedSearch, offset, chip, sort, extraFilters]);  // eslint-disable-line
  useEffect(() => { loadStats(); }, []);

  // Deep-link: `/app/clientes?focus_id=<id>` (notifications + dashboard
  // widget). Distinct from the legacy `?focus=<name>` global-search
  // deep-link above — this one auto-expands the matched customer row and
  // scrolls it into view. The "click the notification → see the new
  // client" UX the operator asked for after the IA auto-cadastra leads.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const focusId = params.get("focus_id");
    if (!focusId) return;
    if (!page.items.length) return;
    const match = page.items.find((c) => c.id === focusId);
    // Always clear the param so a refresh doesn't keep retrying.
    params.delete("focus_id");
    const qs = params.toString();
    window.history.replaceState({}, "",
      window.location.pathname + (qs ? `?${qs}` : ""));
    if (!match) {
      // Customer not in current view — push their id into the search
      // box so the operator at least lands on the right record.
      setChip("");
      setSearch(focusId);
      setOffset(0);
      return;
    }
    setExpanded((prev) => {
      const next = new Set(prev);
      next.add(focusId);
      return next;
    });
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="customer-row-${focusId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-emerald-400");
        setTimeout(() => el.classList.remove("ring-2", "ring-emerald-400"), 2500);
      }
    }, 120);
  }, [page.items]);

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, document_type: form.document ? form.document_type : null };
      let savedCustomer;
      if (editing) {
        const { data } = await api.put(`/customers/${editing.id}`, payload);
        savedCustomer = data;
        toast.success("Cliente atualizado");
      } else {
        const { data } = await api.post("/customers", payload);
        savedCustomer = data;
        toast.success("Cliente criado");
      }
      if (!editing && includeVehicleOnCreate && vehicleForm.identifier.trim()) {
        try {
          await api.post("/vehicles", {
            customer_id: savedCustomer.id,
            identifier: vehicleForm.identifier.trim().toUpperCase().replace(/[\s-]/g, ""),
            brand: vehicleForm.brand || null,
            model: vehicleForm.model || null,
            year: vehicleForm.year ? parseInt(vehicleForm.year, 10) : null,
            color: vehicleForm.color || null,
            km: vehicleForm.km ? parseInt(vehicleForm.km, 10) : null,
            last_oil_change_at: vehicleForm.last_oil_change_at || null,
            last_oil_change_km: vehicleForm.last_oil_change_km ? parseInt(vehicleForm.last_oil_change_km, 10) : null,
            last_service_at: vehicleForm.last_service_at || null,
            next_service_at: vehicleForm.next_service_at || null,
            next_oil_change_at: vehicleForm.next_oil_change_at || null,
            label: [vehicleForm.brand, vehicleForm.model].filter(Boolean).join(" ") || vehicleForm.identifier,
          });
          toast.success("Veículo cadastrado");
        } catch (ve) {
          toast.error(ve?.response?.data?.detail || "Cliente salvo, mas o veículo falhou.");
        }
      }
      closeModal();
      load(); loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    }
  };

  const closeModal = () => {
    setOpen(false); setEditing(null);
    setForm(emptyCustomer);
    setVehicleForm(emptyVehicle);
    setIncludeVehicleOnCreate(false);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/customers/upload_document", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, document_file_url: data.url }));
      toast.success(`Anexado: ${data.filename}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no upload");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name, phone: r.phone, email: r.email || "",
      document: r.document || "", document_type: r.document_type || "cpf",
      document_file_url: r.document_file_url || "",
      address: r.address || "",
      tier: r.tier || "regular",
      birthday: r.birthday || "",
    });
    setOpen(true);
  };

  const remove = async (row) => {
    if (!confirm(`Excluir ${row.name}? Veículos associados também serão removidos.`)) return;
    await api.delete(`/customers/${row.id}`);
    toast.success("Excluído");
    load(); loadStats();
  };

  const toggleSelectOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setSelectAllAcrossPages(false);
  };

  const allOnPageSelected = page.items.length > 0 && page.items.every((c) => selected.has(c.id));
  const someOnPageSelected = page.items.some((c) => selected.has(c.id));

  const toggleSelectPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        page.items.forEach((c) => next.delete(c.id));
      } else {
        page.items.forEach((c) => next.add(c.id));
      }
      return next;
    });
    setSelectAllAcrossPages(false);
  };

  const clearSelection = () => {
    setSelected(new Set());
    setSelectAllAcrossPages(false);
  };

  const bulkDelete = async () => {
    const count = selectAllAcrossPages ? page.total : selected.size;
    if (count === 0) return;
    const filtered = !!debouncedSearch || !!chip;
    // v0.99.49 — texto sector-aware. Só menciona "veículos" quando o
    // tenant realmente trabalha com veículos. Igreja/mandato/clínica
    // etc. veem só o cliente/membro/cidadão.
    const assetWarn = enablesKm ? "Essa ação remove também todos os veículos associados.\n\n" : "";
    const assetWarnShort = enablesKm ? "Veículos associados também serão removidos." : "";
    const warn = selectAllAcrossPages
      ? `Excluir TODOS os ${count.toLocaleString("pt-BR")} clientes${filtered ? " do filtro atual" : ""}?\n\n${assetWarn}Esta operação não pode ser desfeita.`
      : `Excluir ${count} cliente(s) selecionado(s)?${assetWarnShort ? "\n\n" + assetWarnShort : ""}`;
    if (!confirm(warn)) return;
    setBulkDeleting(true);
    try {
      const body = selectAllAcrossPages
        ? { select_all: true, search: debouncedSearch }
        : { ids: Array.from(selected) };
      const { data } = await api.post("/customers/bulk-delete", body);
      const assetMsg = enablesKm && data.assets_deleted
        ? ` e ${data.assets_deleted} veículo(s)` : "";
      toast.success(`${data.deleted} cliente(s)${assetMsg} excluídos.`);
      clearSelection();
      setOffset(0);
      await load(); loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao excluir em massa.");
    } finally {
      setBulkDeleting(false);
    }
  };

  const startConversation = async (cust) => {
    try {
      // The conversations list uses ?focus=<phone> as a deep link
      nav(`/app/conversas?focus=${encodeURIComponent(cust.phone || cust.name)}`);
    } catch { toast.error("Falha ao abrir conversa"); }
  };

  const openAppointment = (cust) => {
    nav(`/app/agendamentos?new=1&customer=${cust.id}`);
  };

  const totalPages = Math.max(1, Math.ceil(page.total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const showingFrom = page.items.length ? offset + 1 : 0;
  const showingTo = offset + page.items.length;

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (extraFilters.brand) n++;
    if (extraFilters.model) n++;
    if (extraFilters.year_from) n++;
    if (extraFilters.year_to) n++;
    return n;
  }, [extraFilters]);

  return (
    <>
      <Topbar
        title={customersPageTitle}
        subtitle={`${page.total ? page.total.toLocaleString("pt-BR") : "—"} contatos · o cérebro que a IA guarda de cada pessoa — histórico, preferências e últimas conversas`}
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => nav("/app/importar")}
              data-testid="customers-import-btn"
              title="Importar planilha (CSV ou Excel)"
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 transition-colors text-[12px] font-medium text-slate-700"
            >
              <UploadSimple size={13} weight="bold" /> Importar
            </button>
            <button
              onClick={() => setExportOpen(true)}
              data-testid="customers-export-btn"
              title="Exportar lista filtrada"
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 transition-colors text-[12px] font-medium text-slate-700"
            >
              <DownloadSimple size={13} weight="bold" /> Exportar
            </button>
            <button
              onClick={() => { setEditing(null); setForm(emptyCustomer); setVehicleForm(emptyVehicle); setIncludeVehicleOnCreate(false); setOpen(true); }}
              data-testid="customers-new-btn"
              title={isClinic ? "Cadastrar novo paciente" : `Cadastrar novo ${enablesAssetTab ? "cliente" : assetSingular}`}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-900 text-white hover:bg-slate-800 transition-colors text-[12px] font-medium shadow-sm"
            >
              <Plus size={13} weight="bold" /> {isClinic ? "Novo paciente" : (enablesAssetTab ? "Novo cliente" : `Novo ${assetSingular}`)}
            </button>
          </div>
        }
      />

      <div className="p-4 md:p-6 lg:p-8 space-y-4">
        {/* ── Stat cards ── */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5" data-testid="customers-stats">
          <StatCard icon={UsersFour}             label="Total"          value={stats.total}         testid="stat-total" />
          <StatCard icon={Sparkle}               label="Novos (30d)"    value={stats.new_30d}
            onClick={() => setChip(chip === "new" ? "" : "new")}
            active={chip === "new"}              testid="stat-new" />
          <StatCard icon={ClockCounterClockwise} label="Inativos"       value={stats.inactive}
            onClick={() => setChip(chip === "inactive" ? "" : "inactive")}
            active={chip === "inactive"}         testid="stat-inactive" />
          <StatCard icon={TrendUp}               label="Oportunidades"  value={stats.opportunities}
            onClick={() => setChip(chip === "opportunity" ? "" : "opportunity")}
            active={chip === "opportunity"}      testid="stat-opportunity" />
          <StatCard icon={Car}                   label={enablesAssetTab ? (isClinic ? "Com prontuário" : `Com ${assetSingular}`) : "Com histórico"} value={stats.with_vehicle}
            onClick={() => setChip(chip === "with_vehicle" ? "" : "with_vehicle")}
            active={chip === "with_vehicle"}     testid="stat-with-vehicle" />
        </div>

        {/* ── Search + Sort + Filter button ── */}
        <div className="flex flex-col md:flex-row md:items-center gap-2.5">
          <div className="relative flex-1 max-w-2xl">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={enablesKm ? "Buscar cliente, telefone, placa ou veículo…" : (isClinic ? "Buscar paciente, telefone ou CPF…" : `Buscar ${assetSingular}, telefone ou CPF…`)}
              data-testid="customers-search"
              className="w-full rounded-full border border-slate-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-slate-900"
            />
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <ArrowsDownUp size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                data-testid="customers-sort"
                className="rounded-full border border-slate-200 bg-white pl-7 pr-7 py-1.5 text-[12px] font-medium text-slate-700 focus:outline-none focus:border-slate-900 appearance-none cursor-pointer"
              >
                {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <button
              type="button"
              onClick={() => setFilterOpen(true)}
              data-testid="customers-filters-btn"
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 transition-colors text-[12px] font-medium text-slate-700"
            >
              <Funnel size={12} weight="bold" /> Filtros
              {activeFilterCount > 0 && (
                <span className="ml-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-slate-900 text-white text-[10px] font-bold">{activeFilterCount}</span>
              )}
            </button>
          </div>
        </div>

        {/* ── Quick chips ── */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="customers-chips">
          {CHIPS
            .filter((c) => {
              // v0.96.24 — filtros de veículo/serviço só fazem sentido em
              // setores com asset separado (automotive/petshop). Em clínica,
              // salão, academia, condomínio, farmácia, contábil etc.
              // esses chips ficam ocultos.
              if (!enablesAssetTab && (c.code === "no_vehicle" || c.code === "service_soon")) return false;
              return true;
            })
            .map((c) => {
            const Icon = c.icon;
            const active = chip === c.code;
            // Rótulos por setor — evita "Sem veículo" pra clínica/salão etc.
            let label = c.label;
            if (c.code === "no_vehicle") {
              if (isClinic) label = "Sem prontuário";
              else if (enablesAssetTab) label = `Sem ${assetSingular}`;
            }
            return (
              <button
                key={c.code || "all"}
                onClick={() => setChip(c.code)}
                data-testid={`chip-${c.code || "all"}`}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border text-[12px] font-semibold transition-all duration-200 ${
                  active
                    ? "bg-slate-900 text-white border-slate-900 shadow-md shadow-slate-900/15"
                    : "bg-white text-slate-700 border-slate-200/80 hover:border-slate-400 hover:shadow-sm"
                }`}
              >
                <Icon size={12} weight="fill" /> {label}
              </button>
            );
          })}
        </div>

        {/* Bulk action bar */}
        {(selected.size > 0 || selectAllAcrossPages) && (
          <div
            className="flex items-center justify-between gap-3 bg-slate-900 text-white rounded-md px-4 py-2.5 shadow-lg shadow-slate-900/10"
            data-testid="bulk-actions-bar"
          >
            <div className="flex items-center gap-3 text-[13px]">
              <span className="font-medium tabular-nums" data-testid="bulk-selected-count">
                {selectAllAcrossPages
                  ? `Todos os ${page.total.toLocaleString("pt-BR")} clientes`
                  : `${selected.size} selecionado(s)`}
              </span>
              {!selectAllAcrossPages && selected.size > 0 && selected.size < page.total && (
                <button
                  onClick={() => setSelectAllAcrossPages(true)}
                  data-testid="bulk-select-all-pages"
                  className="text-[12px] text-slate-300 hover:text-white underline-offset-2 hover:underline"
                >
                  Selecionar todos os {page.total.toLocaleString("pt-BR")}{debouncedSearch ? " do filtro" : ""}
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={bulkDelete}
                disabled={bulkDeleting}
                data-testid="bulk-delete-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rose-600 text-white text-[12px] font-medium hover:bg-rose-700 disabled:opacity-60 transition-colors"
              >
                <Trash size={12} weight="bold" />
                {bulkDeleting ? "Excluindo…" : "Excluir selecionados"}
              </button>
              <button
                onClick={clearSelection}
                data-testid="bulk-cancel-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] text-slate-300 hover:bg-slate-800 transition-colors"
              >
                <X size={12} weight="bold" /> Cancelar
              </button>
            </div>
          </div>
        )}

        {/* ── DESKTOP: table ── */}
        <div className="hidden md:block bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/70">
              <tr className="text-left">
                <th className="w-10 px-3 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    ref={(el) => { if (el) el.indeterminate = !allOnPageSelected && someOnPageSelected; }}
                    onChange={toggleSelectPage}
                    data-testid="bulk-select-page"
                    className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 cursor-pointer"
                  />
                </th>
                <th className="w-8 px-2 py-3"></th>
                <th className="px-4 py-3 text-[11px] font-medium text-slate-600 uppercase tracking-wider">{isClinic ? "Cliente / Paciente" : "Cliente"}</th>
                {/* v0.99.49 — coluna "ativo principal" só pra setores
                    que realmente têm ativo. Igreja/Mandato/Clínica
                    (que usa Prontuário no lugar) ganham tratamento
                    específico. Para Mandato/Igreja a coluna some. */}
                {isClinic ? (
                  <th className="px-4 py-3 text-[11px] font-medium text-slate-600 uppercase tracking-wider">Prontuário</th>
                ) : enablesAssetTab ? (
                  <th className="px-4 py-3 text-[11px] font-medium text-slate-600 uppercase tracking-wider">{`${assetSingularCap} principal`}</th>
                ) : null}
                <th className="px-4 py-3 text-[11px] font-medium text-slate-600 uppercase tracking-wider">Última interação</th>
                <th className="px-4 py-3 text-[11px] font-medium text-slate-600 uppercase tracking-wider">Sinalização</th>
                <th className="px-4 py-3 text-right text-[11px] font-medium text-slate-600 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody>
              {!loading && page.items.length === 0 && (
                <tr><td colSpan={(isClinic || enablesAssetTab) ? 7 : 6} className="p-10 text-center text-slate-500 text-sm" data-testid="customers-empty">
                  {debouncedSearch || chip
                    ? "Nada encontrado pra esse filtro. Tente outra busca ou limpe os chips."
                    : (isClinic
                      ? "Nenhum paciente cadastrado ainda. Comece pelo botão \"Novo paciente\"."
                      : (enablesAssetTab
                        ? "Nenhum cliente cadastrado ainda. Comece pelo botão \"Novo cliente\"."
                        : `Nenhum ${assetSingular} cadastrado ainda. Comece pelo botão de novo cadastro acima.`))}
                </td></tr>
              )}
              {page.items.map((r) => {
                const isOpen = expanded.has(r.id);
                const vehicles = r.vehicles || [];
                return (
                  <FragmentRow
                    key={r.id}
                    row={r}
                    isOpen={isOpen}
                    isSelected={selected.has(r.id) || selectAllAcrossPages}
                    onToggleSelect={() => toggleSelectOne(r.id)}
                    vehicles={vehicles}
                    onToggle={() => toggleExpand(r.id)}
                    onEdit={() => openEdit(r)}
                    onDelete={() => remove(r)}
                    onAddVehicle={() => setVehicleDrawerCustomer(r)}
                    onVehiclesChanged={() => { load(); loadStats(); }}
                    onConversation={() => startConversation(r)}
                    onAppointment={() => openAppointment(r)}
                  />
                );
              })}
            </tbody>
          </table>

          {page.total > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-[12px] text-slate-600" data-testid="customers-pagination">
              <div>
                Mostrando <span className="font-medium text-slate-900 tabular-nums">{showingFrom}–{showingTo}</span> de{" "}
                <span className="font-medium text-slate-900 tabular-nums">{page.total.toLocaleString("pt-BR")}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  disabled={offset === 0 || loading}
                  data-testid="customers-prev-page"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent"
                >
                  <CaretLeft size={12} /> Anterior
                </button>
                <span className="text-[11px] text-slate-500 px-2 tabular-nums" data-testid="customers-page-info">
                  Página {currentPage} de {totalPages}
                </span>
                <button
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                  disabled={!page.has_more || loading}
                  data-testid="customers-next-page"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent"
                >
                  Próxima <CaretRight size={12} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── MOBILE: cards ── */}
        <div className="md:hidden space-y-2" data-testid="customers-cards-mobile">
          {!loading && page.items.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-md p-6 text-center text-slate-500 text-sm">
              {debouncedSearch || chip ? "Nada encontrado." : "Nenhum cliente cadastrado."}
            </div>
          )}
          {page.items.map((r) => (
            <CustomerCard
              key={r.id}
              row={r}
              vehicles={r.vehicles || []}
              isOpen={expanded.has(r.id)}
              onToggle={() => toggleExpand(r.id)}
              onEdit={() => openEdit(r)}
              onConversation={() => startConversation(r)}
              onAppointment={() => openAppointment(r)}
              onDelete={() => remove(r)}
              onAddVehicle={() => setVehicleDrawerCustomer(r)}
              onVehiclesChanged={() => { load(); loadStats(); }}
            />
          ))}
          {page.total > 0 && (
            <div className="flex items-center justify-between px-2 py-3 text-[12px] text-slate-600">
              <span>{showingFrom}–{showingTo} de {page.total.toLocaleString("pt-BR")}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} disabled={offset === 0 || loading} className="px-2 py-1 rounded disabled:opacity-40">
                  <CaretLeft size={14} />
                </button>
                <button onClick={() => setOffset(offset + PAGE_SIZE)} disabled={!page.has_more || loading} className="px-2 py-1 rounded disabled:opacity-40">
                  <CaretRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {open && (
        <CustomerModal
          editing={editing}
          form={form} setForm={setForm}
          vehicleForm={vehicleForm} setVehicleForm={setVehicleForm}
          includeVehicleOnCreate={includeVehicleOnCreate}
          setIncludeVehicleOnCreate={setIncludeVehicleOnCreate}
          uploading={uploading} onUpload={onUpload}
          onSave={save} onClose={closeModal}
          onVehicleAdded={() => { load(); loadStats(); }}
          onVehicleRemoved={() => { load(); loadStats(); }}
          onVehicleUpdated={() => { load(); }}
        />
      )}

      {vehicleDrawerCustomer && (
        <AddVehicleDrawer
          customer={vehicleDrawerCustomer}
          onClose={() => setVehicleDrawerCustomer(null)}
          onSaved={() => { setVehicleDrawerCustomer(null); load(); loadStats(); }}
        />
      )}

      {exportOpen && (
        <ExportModal
          search={debouncedSearch}
          onClose={() => setExportOpen(false)}
        />
      )}

      {filterOpen && (
        <FiltersDrawer
          values={extraFilters}
          onChange={setExtraFilters}
          onClose={() => setFilterOpen(false)}
          onClear={() => { setExtraFilters({ brand: "", model: "", year_from: "", year_to: "" }); }}
        />
      )}
    </>
  );
}

// ─── Desktop expandable row ──────────────────────────────────────────────
function FragmentRow({ row, isOpen, isSelected, onToggleSelect, vehicles, onToggle, onEdit, onDelete, onAddVehicle, onVehiclesChanged, onConversation, onAppointment }) {
  const principal = vehicles[0];
  const lastRel = relativeTime(row.last_interaction_at);
  // v0.99.49 — sector-aware labels na linha. Evita "sem veículo"
  // vazando pra Igreja/Mandato/Clínica/Pet/Salão/Academia.
  const { enablesKm, enablesAssetTab, labels, isMandate, isChurch } = useSector();
  const _assetSingular = labels?.assetSingular?.(false) || "veículo";
  // Para setores sem "ativo" (Igreja/Mandato), não mostramos a coluna
  // de ativo principal — ela some via colspan em outra tabela. Aqui,
  // quando enablesAssetTab=false, a célula do ativo é renderizada
  // como "—" discreto só pra manter o alinhamento.
  const _emptyAssetLabel = enablesAssetTab
    ? `sem ${_assetSingular}`
    : "—";
  const _emptyIdentifier = enablesKm ? "sem placa" : "";
  return (
    <>
      <tr
        className={`border-t border-slate-100 hover:bg-slate-50/70 ${isSelected ? "bg-slate-50" : ""}`}
        data-testid={`customer-row-${row.id}`}
      >
        <td className="w-10 px-3 py-3 text-center">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            data-testid={`customer-select-${row.id}`}
            className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          />
        </td>
        <td className="px-2 py-3 text-center">
          <button onClick={onToggle} data-testid={`customer-expand-${row.id}`} className="p-1 rounded text-slate-500 hover:bg-slate-200 hover:text-slate-900">
            <CaretRight size={12} weight="bold" className={`transition-transform ${isOpen ? "rotate-90" : ""}`} />
          </button>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <div>
              <div className="font-medium text-slate-900 leading-tight flex items-center gap-1.5">
                {row.name}
                {row.tier === "vip" && <VipBadge />}
              </div>
              <div className="text-[11px] text-slate-500 font-mono">{row.phone || "—"}</div>
            </div>
            {row.source && <SourceBadge source={row.source} />}
          </div>
        </td>
        {/* v0.99.49 — coluna de "ativo principal" só pra setores
            que têm ativo. Mandato/Igreja: a coluna é omitida. */}
        {enablesAssetTab && (
        <td className="px-4 py-3 text-slate-700">
          {principal ? (
            <div className="flex items-center gap-2">
              <Car size={13} className="text-slate-500 shrink-0" />
              <div className="leading-tight">
                <div className="text-[12.5px]">
                  {[principal.brand, principal.model].filter(Boolean).join(" ") || principal.label || "—"}
                  {principal.year && <span className="text-slate-500"> · {principal.year}</span>}
                </div>
                <div className="text-[10.5px] text-slate-500 font-mono">{principal.identifier || _emptyIdentifier}</div>
              </div>
              {vehicles.length > 1 && (
                <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">+{vehicles.length - 1}</span>
              )}
            </div>
          ) : (
            <span className="text-[12px] text-slate-400 italic">{_emptyAssetLabel}</span>
          )}
        </td>
        )}
        <td className="px-4 py-3 text-slate-700">
          {lastRel ? (
            <div className="leading-tight">
              <div className="text-[12.5px] text-slate-700">{lastRel}</div>
              <div className="text-[10px] text-slate-500">{fmtDate(row.last_interaction_at)}</div>
            </div>
          ) : (
            <span className="text-[12px] text-slate-400 italic">nunca</span>
          )}
        </td>
        <td className="px-4 py-3">
          <OpportunityBadge tag={row.opportunity_tag} enablesAssetTab={enablesAssetTab} assetSingular={_assetSingular} />
        </td>
        <td className="px-4 py-3 text-right">
          <div className="inline-flex items-center gap-0.5">
            <RowAction icon={ChatCircleDots} title="Abrir conversa"  onClick={onConversation} testid={`customer-chat-${row.id}`} />
            <RowAction icon={CalendarPlus}    title="Agendar"         onClick={onAppointment}  testid={`customer-appoint-${row.id}`} />
            <RowAction icon={PencilSimple}    title="Editar"          onClick={onEdit}         testid={`customer-edit-${row.id}`} />
            <RowAction icon={Trash}           title="Excluir"         tone="rose"
              onClick={onDelete}                                                                testid={`customer-delete-${row.id}`} />
          </div>
        </td>
      </tr>
      {isOpen && enablesAssetTab && (
        <tr className="bg-slate-50/40" data-testid={`customer-vehicles-${row.id}`}>
          <td></td><td></td>
          <td colSpan="5" className="px-4 py-3">
            <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 mb-2 font-medium">{labels?.assetPlural?.(true) || "Veículos"}</div>
            {vehicles.length === 0 ? (
              <div className="text-[13px] text-slate-500 italic mb-2">{`Nenhum ${_assetSingular} cadastrado pra esse cliente.`}</div>
            ) : (
              <div className="space-y-1.5 mb-3">
                {vehicles.map((v) => (
                  <VehicleInlineCard key={v.id} vehicle={v} onChanged={onVehiclesChanged} />
                ))}
              </div>
            )}
            <button onClick={onAddVehicle} data-testid={`customer-add-vehicle-${row.id}`} className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-700 hover:text-slate-900">
              <Plus size={11} weight="bold" /> Adicionar veículo
            </button>
          </td>
        </tr>
      )}
    </>
  );
}

function RowAction({ icon: Icon, title, onClick, testid, tone }) {
  const cls = tone === "rose"
    ? "p-1.5 rounded hover:bg-rose-50 text-slate-500 hover:text-rose-600"
    : "p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-900";
  return (
    <button onClick={onClick} title={title} data-testid={testid} className={cls}>
      <Icon size={14} />
    </button>
  );
}

// ─── Mobile compact card ─────────────────────────────────────────────────
function CustomerCard({ row, vehicles, isOpen, onToggle, onEdit, onConversation, onAppointment, onDelete, onAddVehicle, onVehiclesChanged }) {
  const principal = vehicles[0];
  const lastRel = relativeTime(row.last_interaction_at);
  const { enablesAssetTab, labels } = useSector();
  const _assetSingular = labels?.assetSingular?.(false) || "veículo";
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3.5" data-testid={`customer-card-${row.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1" onClick={onToggle}>
          <div className="flex items-center gap-1.5">
            <div className="font-medium text-slate-900 truncate">{row.name}</div>
            {row.tier === "vip" && <VipBadge />}
            {row.source && <SourceBadge source={row.source} />}
          </div>
          <div className="text-[11.5px] text-slate-500 font-mono mt-0.5">{row.phone || "—"}</div>
        </div>
        <button onClick={onEdit} className="p-1.5 text-slate-400 hover:text-slate-900">
          <PencilSimple size={14} />
        </button>
      </div>

      <div className="mt-2 flex items-center gap-1.5 flex-wrap">
        <OpportunityBadge tag={row.opportunity_tag} enablesAssetTab={enablesAssetTab} assetSingular={_assetSingular} />
        {lastRel && (
          <span className="inline-flex items-center gap-1 text-[10.5px] text-slate-500">
            <ClockCounterClockwise size={10} /> {lastRel}
          </span>
        )}
      </div>

      {principal && enablesAssetTab && (
        <div className="mt-2 flex items-center gap-2 text-[12px] text-slate-700">
          <Car size={12} className="text-slate-500" />
          <span className="truncate">
            {[principal.brand, principal.model].filter(Boolean).join(" ") || principal.label || "Veículo"}
          </span>
          <span className="text-[10.5px] font-mono text-slate-500">{principal.identifier}</span>
          {vehicles.length > 1 && (
            <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full ml-auto">+{vehicles.length - 1}</span>
          )}
        </div>
      )}

      <div className="mt-3 flex items-center gap-1.5">
        <button onClick={onConversation} className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-full bg-slate-900 text-white text-[11.5px] font-medium hover:bg-slate-800">
          <ChatCircleDots size={12} weight="fill" /> Conversa
        </button>
        <button onClick={onAppointment} className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-full border border-slate-200 bg-white text-slate-800 text-[11.5px] font-medium hover:bg-slate-50">
          <CalendarPlus size={12} weight="fill" /> Agendar
        </button>
        {enablesAssetTab && (
          <button onClick={onToggle} title={`Ver ${_assetSingular}s`} className="px-2 py-1.5 rounded-full border border-slate-200 text-slate-700 text-[11.5px]">
            <Car size={12} weight="fill" />
          </button>
        )}
      </div>

      {isOpen && enablesAssetTab && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-2">{labels?.assetPlural?.(true) || "Veículos"}</div>
          {vehicles.length === 0 ? (
            <div className="text-[12px] text-slate-500 italic mb-2">{`Nenhum ${_assetSingular} cadastrado.`}</div>
          ) : (
            <div className="space-y-1.5 mb-2">
              {vehicles.map((v) => <VehicleInlineCard key={v.id} vehicle={v} onChanged={onVehiclesChanged} />)}
            </div>
          )}
          <button onClick={onAddVehicle} className="inline-flex items-center gap-1 text-[12px] font-medium text-slate-700 hover:text-slate-900">
            <Plus size={11} weight="bold" /> Adicionar {_assetSingular}
          </button>
        </div>
      )}
    </div>
  );
}

function VehicleInlineCard({ vehicle, onChanged }) {
  const label = [vehicle.brand, vehicle.model].filter(Boolean).join(" ") || vehicle.label || "Veículo";
  const handleDelete = async () => {
    if (!confirm(`Remover ${label} (${vehicle.identifier})?`)) return;
    try {
      await api.delete(`/vehicles/${vehicle.id}`);
      toast.success("Veículo removido");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao remover");
    }
  };
  return (
    <div className="flex items-center gap-3 bg-white rounded-md border border-slate-200 px-3 py-2" data-testid={`vehicle-inline-${vehicle.id}`}>
      <Car size={16} className="text-slate-500 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium text-slate-900 truncate">
          {label}
          {vehicle.year && <span className="text-slate-500 font-normal"> · {vehicle.year}</span>}
        </div>
        <div className="text-[11px] text-slate-500 font-mono">
          {vehicle.identifier || "sem placa"}
          {vehicle.km && <span className="font-sans"> · {Number(vehicle.km).toLocaleString("pt-BR")} km</span>}
          {vehicle.last_oil_change_at && <span className="font-sans"> · óleo {fmtDate(vehicle.last_oil_change_at)}</span>}
        </div>
      </div>
      <button onClick={handleDelete} data-testid={`vehicle-delete-${vehicle.id}`} className="p-1.5 rounded text-slate-400 hover:bg-rose-50 hover:text-rose-600 shrink-0">
        <Trash size={13} />
      </button>
    </div>
  );
}

// ─── Filters drawer (vehicle-only for now, exposes future fields cleanly) ──
function FiltersDrawer({ values, onChange, onClose, onClear }) {
  const [local, setLocal] = useState(values);
  const apply = () => { onChange(local); onClose(); };
  return (
    <PremiumModal
      icon={Funnel}
      title="Filtros avançados"
      subtitle="Refine pelo veículo do cliente — os chips do topo já cobrem status, relacionamento e período."
      size="sm"
      onClose={onClose}
      testid="filters-drawer"
      footer={(
        <>
          <button onClick={onClear} className="text-[12.5px] font-medium text-slate-500 hover:text-slate-900 mr-auto" data-testid="filter-clear">
            Limpar tudo
          </button>
          <button onClick={onClose} className="text-[12.5px] font-medium text-slate-600 px-3 py-1.5 rounded-full hover:bg-slate-100">
            Cancelar
          </button>
          <button onClick={apply} data-testid="filter-apply" className="inline-flex items-center px-4 py-1.5 rounded-full bg-slate-900 text-white text-[12.5px] font-semibold hover:bg-slate-800 shadow-sm shadow-slate-900/15 transition-colors">
            Aplicar
          </button>
        </>
      )}
    >
      <section>
        <div className="text-[10.5px] uppercase tracking-[0.12em] text-slate-500 font-semibold mb-3">Veículo</div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block col-span-2">
            <FieldLabel>Marca</FieldLabel>
            <input value={local.brand} onChange={(e) => setLocal({ ...local, brand: e.target.value })} placeholder="BYD" className={inputCls} data-testid="filter-brand" />
          </label>
          <label className="block col-span-2">
            <FieldLabel>Modelo</FieldLabel>
            <input value={local.model} onChange={(e) => setLocal({ ...local, model: e.target.value })} placeholder="Song Pro" className={inputCls} data-testid="filter-model" />
          </label>
          <label className="block">
            <FieldLabel>Ano de</FieldLabel>
            <input type="number" value={local.year_from} onChange={(e) => setLocal({ ...local, year_from: e.target.value })} placeholder="2020" className={inputCls} data-testid="filter-year-from" />
          </label>
          <label className="block">
            <FieldLabel>até</FieldLabel>
            <input type="number" value={local.year_to} onChange={(e) => setLocal({ ...local, year_to: e.target.value })} placeholder="2024" className={inputCls} data-testid="filter-year-to" />
          </label>
        </div>
      </section>
    </PremiumModal>
  );
}

// ─── Customer create/edit modal ──────────────────────────────────────────
function CustomerModal({
  editing, form, setForm, vehicleForm, setVehicleForm,
  includeVehicleOnCreate, setIncludeVehicleOnCreate,
  uploading, onUpload, onSave, onClose,
  onVehicleAdded, onVehicleRemoved, onVehicleUpdated,
}) {
  const formId = "customer-form";
  // v0.98 · Fev/2026 — Multi-sector: só mostra seção de veículo pra
  // tenants automotivos. Pet shop, salão, clínica etc. não veem "Já
  // cadastrar um veículo" (não faz sentido no contexto).
  const { isAutomotive, enablesAssetTab, labels } = useSector();
  const showVehicleSection = isAutomotive && enablesAssetTab;
  const assetSingular = labels.assetSingular();
  // ─── Vehicles attached to the customer (edit mode only) ─────────────
  const [editVehicles, setEditVehicles] = useState([]);
  const [editVehiclesLoading, setEditVehiclesLoading] = useState(false);
  const [editingVehicleId, setEditingVehicleId] = useState(null);
  const [editingVehicleForm, setEditingVehicleForm] = useState(emptyVehicle);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [newVehicleForm, setNewVehicleForm] = useState(emptyVehicle);
  const [vehicleSaving, setVehicleSaving] = useState(false);

  useEffect(() => {
    if (!editing) return;
    let cancelled = false;
    setEditVehiclesLoading(true);
    api.get(`/vehicles?customer_id=${editing.id}`)
      .then(({ data }) => { if (!cancelled) setEditVehicles(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setEditVehicles([]); })
      .finally(() => { if (!cancelled) setEditVehiclesLoading(false); });
    return () => { cancelled = true; };
  }, [editing]);

  const startEditVehicle = (v) => {
    setEditingVehicleId(v.id);
    setEditingVehicleForm({
      identifier: v.identifier || "",
      brand: v.brand || "",
      model: v.model || "",
      year: v.year || "",
      color: v.color || "",
      km: v.km || "",
      last_oil_change_at: (v.last_oil_change_at || "").slice(0, 10),
      last_oil_change_km: v.last_oil_change_km || "",
      last_service_at: (v.last_service_at || "").slice(0, 10),
      next_service_at: (v.next_service_at || "").slice(0, 10),
      next_oil_change_at: (v.next_oil_change_at || "").slice(0, 10),
    });
  };

  const saveVehicleEdit = async () => {
    if (!editingVehicleForm.identifier.trim()) { toast.error("Informe a placa."); return; }
    setVehicleSaving(true);
    try {
      const payload = {
        ...editingVehicleForm,
        year: editingVehicleForm.year ? Number(editingVehicleForm.year) : null,
        km: editingVehicleForm.km ? Number(editingVehicleForm.km) : null,
        last_oil_change_km: editingVehicleForm.last_oil_change_km ? Number(editingVehicleForm.last_oil_change_km) : null,
        last_oil_change_at: editingVehicleForm.last_oil_change_at || null,
        last_service_at: editingVehicleForm.last_service_at || null,
        next_service_at: editingVehicleForm.next_service_at || null,
        next_oil_change_at: editingVehicleForm.next_oil_change_at || null,
      };
      const { data } = await api.put(`/vehicles/${editingVehicleId}`, payload);
      setEditVehicles((prev) => prev.map((v) => (v.id === editingVehicleId ? data : v)));
      setEditingVehicleId(null);
      onVehicleUpdated?.();
      toast.success("Veículo atualizado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível salvar o veículo.");
    } finally {
      setVehicleSaving(false);
    }
  };

  const removeVehicle = async (v) => {
    if (!window.confirm(`Remover ${v.identifier || "este veículo"}?`)) return;
    try {
      await api.delete(`/vehicles/${v.id}`);
      setEditVehicles((prev) => prev.filter((x) => x.id !== v.id));
      onVehicleRemoved?.();
      toast.success("Veículo removido");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível remover.");
    }
  };

  const addVehicle = async () => {
    if (!newVehicleForm.identifier.trim()) { toast.error("Informe a placa."); return; }
    setVehicleSaving(true);
    try {
      const payload = {
        customer_id: editing.id,
        ...newVehicleForm,
        year: newVehicleForm.year ? Number(newVehicleForm.year) : null,
        km: newVehicleForm.km ? Number(newVehicleForm.km) : null,
        last_oil_change_km: newVehicleForm.last_oil_change_km ? Number(newVehicleForm.last_oil_change_km) : null,
        last_oil_change_at: newVehicleForm.last_oil_change_at || null,
        last_service_at: newVehicleForm.last_service_at || null,
        next_service_at: newVehicleForm.next_service_at || null,
        next_oil_change_at: newVehicleForm.next_oil_change_at || null,
      };
      const { data } = await api.post(`/vehicles`, payload);
      setEditVehicles((prev) => [...prev, data]);
      setNewVehicleForm(emptyVehicle);
      setShowAddVehicle(false);
      onVehicleAdded?.();
      toast.success("Veículo adicionado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível adicionar.");
    } finally {
      setVehicleSaving(false);
    }
  };
  return (
    <PremiumModal
      icon={editing ? PencilSimple : Plus}
      title={editing ? "Editar cliente" : "Novo cliente"}
      subtitle={editing
        ? "Atualize os dados que mudaram — endereço, telefone, documento."
        : (showVehicleSection
          ? "Cadastre o cliente e, se quiser, já vincule um veículo no mesmo fluxo."
          : "Cadastre o cliente com as informações principais.")}
      onClose={onClose}
      size="lg"
      testid="customer-modal"
      footer={(
        <>
          <button type="button" onClick={onClose} className="text-[12.5px] font-medium text-slate-600 px-3 py-1.5 rounded-full hover:bg-slate-100">
            Cancelar
          </button>
          <button type="submit" form={formId} className="inline-flex items-center px-5 py-2 rounded-full bg-slate-900 text-white hover:bg-slate-800 transition-colors text-[12.5px] font-semibold shadow-sm shadow-slate-900/15" data-testid="customer-form-submit">
            {editing ? "Salvar alterações" : "Criar cliente"}
          </button>
        </>
      )}
    >
      <form id={formId} onSubmit={onSave} className="space-y-4">
        <label className="block">
          <FieldLabel>Nome completo</FieldLabel>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="customer-form-name" placeholder="Ex: Rafael Almeida" />
        </label>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block">
            <FieldLabel>Telefone</FieldLabel>
            <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="customer-form-phone" placeholder="(11) 99999-0000" />
          </label>
          <label className="block">
            <FieldLabel>Email</FieldLabel>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="customer-form-email" placeholder="opcional" />
          </label>
        </div>

        <label className="block">
          <FieldLabel>Data de nascimento</FieldLabel>
          <input
            type="date"
            value={form.birthday || ""}
            onChange={(e) => setForm({ ...form, birthday: e.target.value })}
            data-testid="customer-form-birthday"
            className={inputCls}
            max={new Date().toISOString().slice(0, 10)}
          />
        </label>

        <label className="block">
          <FieldLabel>Endereço</FieldLabel>
          <textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} placeholder="Logradouro, número, bairro, cidade — UF" data-testid="customer-form-address" className={inputCls + " resize-none"} />
        </label>

        <div className="grid grid-cols-[110px_1fr] gap-3">
          <label className="block">
            <FieldLabel>Tipo</FieldLabel>
            <select value={form.document_type} onChange={(e) => setForm({ ...form, document_type: e.target.value })} data-testid="customer-form-document-type" className={inputCls}>
              {DOC_TYPES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </label>
          <label className="block min-w-0">
            <FieldLabel>Documento</FieldLabel>
            <input value={form.document} onChange={(e) => setForm({ ...form, document: e.target.value })} placeholder={form.document_type === "cnpj" ? "00.000.000/0000-00" : "000.000.000-00"} className={inputCls} data-testid="customer-form-document" />
          </label>
        </div>

        <div>
          <FieldLabel>Anexar documento</FieldLabel>
          <p className="text-[11.5px] text-slate-500 mt-0.5 leading-snug">Foto ou PDF — máx 10 MB. Opcional.</p>
          {form.document_file_url ? (
            <div className="mt-2 flex items-center justify-between gap-3 px-3.5 py-2.5 border border-slate-200 rounded-xl bg-slate-50/70" data-testid="customer-form-document-attachment">
              <a href={form.document_file_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[13px] text-slate-800 hover:text-slate-900 truncate min-w-0">
                <FilePdf size={16} weight="regular" className="shrink-0" />
                <span className="truncate">Documento anexado — abrir</span>
              </a>
              <button type="button" onClick={() => setForm({ ...form, document_file_url: "" })} data-testid="customer-form-document-remove" className="p-1.5 rounded-full text-slate-400 hover:text-rose-600 hover:bg-rose-50 shrink-0">
                <X size={14} />
              </button>
            </div>
          ) : (
            <label className="mt-2 flex items-center justify-center gap-2 px-3 py-3.5 border-2 border-dashed border-slate-200 rounded-xl text-[13px] text-slate-600 hover:border-slate-900 hover:text-slate-900 hover:bg-slate-50/50 cursor-pointer transition-all" data-testid="customer-form-document-upload">
              <Paperclip size={14} />
              {uploading ? "Enviando…" : "Selecionar arquivo (PDF, JPG, PNG)"}
              <input type="file" accept=".pdf,image/*" onChange={onUpload} disabled={uploading} className="hidden" />
            </label>
          )}
        </div>

        <div className="pt-4 border-t border-slate-100">
          <label className="flex items-start gap-2.5 cursor-pointer" data-testid="customer-form-tier-vip">
            <input
              type="checkbox"
              checked={form.tier === "vip"}
              onChange={(e) => setForm({ ...form, tier: e.target.checked ? "vip" : "regular" })}
              className="mt-0.5 w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
            />
            <div className="min-w-0">
              <div className="text-[13.5px] font-medium text-slate-900 flex items-center gap-2">
                Cliente VIP
                {form.tier === "vip" && (
                  <span className="text-[9.5px] uppercase tracking-[0.16em] font-bold text-white bg-slate-900 px-1.5 py-0.5 rounded-full">
                    ativado
                  </span>
                )}
              </div>
              <div className="text-[11.5px] text-slate-500 leading-snug">
                Recebe áudio humanizado, ofertas exclusivas e prioridade nas automações. Use pra clientes frequentes ou de alto ticket.
              </div>
            </div>
          </label>
        </div>

        {!editing && showVehicleSection ? (
          <div className="pt-4 border-t border-slate-100">
            <label className="flex items-start gap-2.5 cursor-pointer" data-testid="customer-form-include-vehicle">
              <input type="checkbox" checked={includeVehicleOnCreate} onChange={(e) => setIncludeVehicleOnCreate(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900" />
              <div className="min-w-0">
                <div className="text-[13.5px] font-medium text-slate-900">Já cadastrar um veículo</div>
                <div className="text-[11.5px] text-slate-500 leading-snug">Preencha só o que tiver em mãos — o resto dá pra completar depois.</div>
              </div>
            </label>
            {includeVehicleOnCreate && (
              <div className="mt-4 space-y-3 pl-1" data-testid="customer-form-vehicle-section">
                <div className="grid grid-cols-2 gap-3">
                  <label className="block"><FieldLabel>Placa</FieldLabel><input value={vehicleForm.identifier} onChange={(e) => setVehicleForm({ ...vehicleForm, identifier: e.target.value })} placeholder="ABC1D23" data-testid="customer-form-vehicle-plate" className={inputClsMono} /></label>
                  <label className="block"><FieldLabel>Ano</FieldLabel><input type="number" value={vehicleForm.year} onChange={(e) => setVehicleForm({ ...vehicleForm, year: e.target.value })} placeholder="2024" data-testid="customer-form-vehicle-year" className={inputCls} /></label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block"><FieldLabel>Marca</FieldLabel>
                    <Combobox
                      value={vehicleForm.brand}
                      onChange={(v) => setVehicleForm({ ...vehicleForm, brand: v })}
                      options={CAR_BRANDS}
                      placeholder="BYD"
                      inputClassName={inputCls}
                      testid="customer-form-vehicle-brand"
                    />
                  </label>
                  <label className="block"><FieldLabel>Modelo</FieldLabel>
                    <Combobox
                      value={vehicleForm.model}
                      onChange={(v) => setVehicleForm({ ...vehicleForm, model: v })}
                      options={modelsForBrand(vehicleForm.brand)}
                      placeholder="Song Pro"
                      inputClassName={inputCls}
                      testid="customer-form-vehicle-model"
                    />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block"><FieldLabel>Cor</FieldLabel>
                    <Combobox
                      value={vehicleForm.color}
                      onChange={(v) => setVehicleForm({ ...vehicleForm, color: v })}
                      options={VEHICLE_COLORS}
                      placeholder="Preto"
                      inputClassName={inputCls}
                      testid="customer-form-vehicle-color"
                    />
                  </label>
                  <label className="block"><FieldLabel>KM atual</FieldLabel><input type="number" value={vehicleForm.km} onChange={(e) => setVehicleForm({ ...vehicleForm, km: e.target.value })} placeholder="32.000" className={inputCls} /></label>
                </div>
                {/* v0.98 · Histórico de manutenção (mesmos campos do importador) */}
                <div className="pt-3 mt-1 border-t border-slate-100">
                  <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-2">
                    Histórico de manutenção <span className="text-slate-400 normal-case tracking-normal font-normal">(opcional)</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block"><FieldLabel>Última troca de óleo</FieldLabel><input type="date" value={vehicleForm.last_oil_change_at} onChange={(e) => setVehicleForm({ ...vehicleForm, last_oil_change_at: e.target.value })} className={inputCls} data-testid="customer-form-vehicle-last-oil-at" /></label>
                    <label className="block"><FieldLabel>KM da troca de óleo</FieldLabel><input type="number" value={vehicleForm.last_oil_change_km} onChange={(e) => setVehicleForm({ ...vehicleForm, last_oil_change_km: e.target.value })} placeholder="28.500" className={inputCls} data-testid="customer-form-vehicle-last-oil-km" /></label>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <label className="block"><FieldLabel>Última revisão</FieldLabel><input type="date" value={vehicleForm.last_service_at} onChange={(e) => setVehicleForm({ ...vehicleForm, last_service_at: e.target.value })} className={inputCls} data-testid="customer-form-vehicle-last-service" /></label>
                    <label className="block"><FieldLabel>Próxima revisão prevista</FieldLabel><input type="date" value={vehicleForm.next_service_at} onChange={(e) => setVehicleForm({ ...vehicleForm, next_service_at: e.target.value })} className={inputCls} data-testid="customer-form-vehicle-next-service" /></label>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (editing && showVehicleSection) ? (
          // ─── Edit mode: vehicles attached to the customer ────────────
          <div className="pt-4 border-t border-slate-100" data-testid="customer-edit-vehicles">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">
                Veículos {editVehicles.length > 0 && <span className="text-slate-400">· {editVehicles.length}</span>}
              </div>
              {!showAddVehicle && (
                <button
                  type="button"
                  onClick={() => setShowAddVehicle(true)}
                  className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-slate-700 hover:text-slate-900 px-2 py-1 rounded-full hover:bg-slate-100 transition-colors"
                  data-testid="customer-edit-add-vehicle"
                >
                  <Plus size={11} weight="bold" /> Adicionar veículo
                </button>
              )}
            </div>

            {editVehiclesLoading ? (
              <div className="text-[12px] text-slate-400 py-3">Carregando veículos…</div>
            ) : editVehicles.length === 0 && !showAddVehicle ? (
              <div className="text-[12.5px] text-slate-500 italic bg-slate-50 border border-slate-100 rounded-lg p-3">
                Nenhum veículo vinculado a este cliente ainda.
              </div>
            ) : (
              <div className="space-y-2">
                {editVehicles.map((v) => (
                  <div key={v.id} className="rounded-lg border border-slate-200 bg-white" data-testid={`customer-edit-vehicle-${v.id}`}>
                    {editingVehicleId === v.id ? (
                      <div className="p-3 space-y-2.5">
                        <div className="grid grid-cols-2 gap-2.5">
                          <label className="block"><FieldLabel>Placa</FieldLabel><input value={editingVehicleForm.identifier} onChange={(e) => setEditingVehicleForm({ ...editingVehicleForm, identifier: e.target.value })} className={inputClsMono} data-testid={`vehicle-edit-${v.id}-plate`} /></label>
                          <label className="block"><FieldLabel>Ano</FieldLabel><input type="number" value={editingVehicleForm.year} onChange={(e) => setEditingVehicleForm({ ...editingVehicleForm, year: e.target.value })} className={inputCls} /></label>
                        </div>
                        <div className="grid grid-cols-2 gap-2.5">
                          <label className="block"><FieldLabel>Marca</FieldLabel>
                            <Combobox
                              value={editingVehicleForm.brand}
                              onChange={(b) => setEditingVehicleForm({ ...editingVehicleForm, brand: b })}
                              options={CAR_BRANDS}
                              inputClassName={inputCls}
                              testid={`vehicle-edit-${v.id}-brand`}
                            />
                          </label>
                          <label className="block"><FieldLabel>Modelo</FieldLabel>
                            <Combobox
                              value={editingVehicleForm.model}
                              onChange={(m) => setEditingVehicleForm({ ...editingVehicleForm, model: m })}
                              options={modelsForBrand(editingVehicleForm.brand)}
                              inputClassName={inputCls}
                              testid={`vehicle-edit-${v.id}-model`}
                            />
                          </label>
                        </div>
                        <div className="grid grid-cols-2 gap-2.5">
                          <label className="block"><FieldLabel>Cor</FieldLabel>
                            <Combobox
                              value={editingVehicleForm.color}
                              onChange={(c) => setEditingVehicleForm({ ...editingVehicleForm, color: c })}
                              options={VEHICLE_COLORS}
                              inputClassName={inputCls}
                              testid={`vehicle-edit-${v.id}-color`}
                            />
                          </label>
                          <label className="block"><FieldLabel>KM atual</FieldLabel><input type="number" value={editingVehicleForm.km} onChange={(e) => setEditingVehicleForm({ ...editingVehicleForm, km: e.target.value })} className={inputCls} /></label>
                        </div>
                        {/* v0.98 · Histórico de manutenção */}
                        <div className="pt-2 mt-2 border-t border-slate-100 space-y-2">
                          <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">
                            Histórico de manutenção <span className="text-slate-400 normal-case tracking-normal font-normal">(opcional)</span>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <label className="block"><FieldLabel>Última troca óleo</FieldLabel><input type="date" value={editingVehicleForm.last_oil_change_at} onChange={(e) => setEditingVehicleForm({ ...editingVehicleForm, last_oil_change_at: e.target.value })} className={inputCls} data-testid={`vehicle-edit-${v.id}-last-oil-at`} /></label>
                            <label className="block"><FieldLabel>KM da troca</FieldLabel><input type="number" value={editingVehicleForm.last_oil_change_km} onChange={(e) => setEditingVehicleForm({ ...editingVehicleForm, last_oil_change_km: e.target.value })} className={inputCls} data-testid={`vehicle-edit-${v.id}-last-oil-km`} /></label>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <label className="block"><FieldLabel>Última revisão</FieldLabel><input type="date" value={editingVehicleForm.last_service_at} onChange={(e) => setEditingVehicleForm({ ...editingVehicleForm, last_service_at: e.target.value })} className={inputCls} data-testid={`vehicle-edit-${v.id}-last-service`} /></label>
                            <label className="block"><FieldLabel>Próxima revisão</FieldLabel><input type="date" value={editingVehicleForm.next_service_at} onChange={(e) => setEditingVehicleForm({ ...editingVehicleForm, next_service_at: e.target.value })} className={inputCls} data-testid={`vehicle-edit-${v.id}-next-service`} /></label>
                          </div>
                        </div>
                        <div className="flex items-center justify-end gap-2 pt-1">
                          <button type="button" onClick={() => setEditingVehicleId(null)} className="text-[11.5px] font-medium text-slate-500 hover:text-slate-900 px-2.5 py-1 rounded-full">Cancelar</button>
                          <button type="button" onClick={saveVehicleEdit} disabled={vehicleSaving} className="inline-flex items-center px-3 py-1 rounded-full bg-slate-900 text-white text-[11.5px] font-semibold hover:bg-slate-800 disabled:opacity-50" data-testid={`vehicle-edit-${v.id}-save`}>
                            {vehicleSaving ? "Salvando…" : "Salvar"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between px-3 py-2.5">
                        <div className="min-w-0 flex-1">
                          <div className="text-[12.5px] font-semibold text-slate-900 font-mono">{v.identifier || "—"}</div>
                          <div className="text-[11.5px] text-slate-500 truncate font-sans">
                            {[v.brand, v.model, v.year, v.color].filter(Boolean).join(" · ") || <span className="italic text-slate-400">sem detalhes</span>}
                            {v.km ? <span> · {Number(v.km).toLocaleString("pt-BR")} km</span> : null}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button type="button" onClick={() => startEditVehicle(v)} className="p-1.5 rounded text-slate-500 hover:bg-slate-100 hover:text-slate-900" data-testid={`vehicle-edit-${v.id}`} title="Editar">
                            <PencilSimple size={13} />
                          </button>
                          <button type="button" onClick={() => removeVehicle(v)} className="p-1.5 rounded text-slate-400 hover:bg-rose-50 hover:text-rose-600" data-testid={`vehicle-remove-${v.id}`} title="Remover">
                            <Trash size={13} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {showAddVehicle && (
              <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-2.5" data-testid="customer-edit-add-vehicle-form">
                <div className="grid grid-cols-2 gap-2.5">
                  <label className="block"><FieldLabel>Placa</FieldLabel><input value={newVehicleForm.identifier} onChange={(e) => setNewVehicleForm({ ...newVehicleForm, identifier: e.target.value })} placeholder="ABC1D23" data-testid="customer-edit-new-vehicle-plate" className={inputClsMono} /></label>
                  <label className="block"><FieldLabel>Ano</FieldLabel><input type="number" value={newVehicleForm.year} onChange={(e) => setNewVehicleForm({ ...newVehicleForm, year: e.target.value })} placeholder="2024" className={inputCls} /></label>
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  <label className="block"><FieldLabel>Marca</FieldLabel>
                    <Combobox
                      value={newVehicleForm.brand}
                      onChange={(v) => setNewVehicleForm({ ...newVehicleForm, brand: v })}
                      options={CAR_BRANDS}
                      placeholder="BYD"
                      inputClassName={inputCls}
                      testid="customer-edit-new-vehicle-brand"
                    />
                  </label>
                  <label className="block"><FieldLabel>Modelo</FieldLabel>
                    <Combobox
                      value={newVehicleForm.model}
                      onChange={(m) => setNewVehicleForm({ ...newVehicleForm, model: m })}
                      options={modelsForBrand(newVehicleForm.brand)}
                      placeholder="Song Pro"
                      inputClassName={inputCls}
                      testid="customer-edit-new-vehicle-model"
                    />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  <label className="block"><FieldLabel>Cor</FieldLabel>
                    <Combobox
                      value={newVehicleForm.color}
                      onChange={(c) => setNewVehicleForm({ ...newVehicleForm, color: c })}
                      options={VEHICLE_COLORS}
                      placeholder="Preto"
                      inputClassName={inputCls}
                      testid="customer-edit-new-vehicle-color"
                    />
                  </label>
                  <label className="block"><FieldLabel>KM atual</FieldLabel><input type="number" value={newVehicleForm.km} onChange={(e) => setNewVehicleForm({ ...newVehicleForm, km: e.target.value })} placeholder="32.000" className={inputCls} /></label>
                </div>
                {/* v0.98 · Histórico de manutenção (add-vehicle) */}
                <div className="pt-3 mt-1 border-t border-slate-100">
                  <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-2">
                    Histórico de manutenção <span className="text-slate-400 normal-case tracking-normal font-normal">(opcional)</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block"><FieldLabel>Última troca de óleo</FieldLabel><input type="date" value={newVehicleForm.last_oil_change_at} onChange={(e) => setNewVehicleForm({ ...newVehicleForm, last_oil_change_at: e.target.value })} className={inputCls} data-testid="customer-new-vehicle-last-oil-at" /></label>
                    <label className="block"><FieldLabel>KM da troca de óleo</FieldLabel><input type="number" value={newVehicleForm.last_oil_change_km} onChange={(e) => setNewVehicleForm({ ...newVehicleForm, last_oil_change_km: e.target.value })} placeholder="28.500" className={inputCls} data-testid="customer-new-vehicle-last-oil-km" /></label>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <label className="block"><FieldLabel>Última revisão</FieldLabel><input type="date" value={newVehicleForm.last_service_at} onChange={(e) => setNewVehicleForm({ ...newVehicleForm, last_service_at: e.target.value })} className={inputCls} data-testid="customer-new-vehicle-last-service" /></label>
                    <label className="block"><FieldLabel>Próxima revisão prevista</FieldLabel><input type="date" value={newVehicleForm.next_service_at} onChange={(e) => setNewVehicleForm({ ...newVehicleForm, next_service_at: e.target.value })} className={inputCls} data-testid="customer-new-vehicle-next-service" /></label>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2 pt-1">
                  <button type="button" onClick={() => { setShowAddVehicle(false); setNewVehicleForm(emptyVehicle); }} className="text-[11.5px] font-medium text-slate-500 hover:text-slate-900 px-2.5 py-1 rounded-full">Cancelar</button>
                  <button type="button" onClick={addVehicle} disabled={vehicleSaving} className="inline-flex items-center px-3 py-1 rounded-full bg-slate-900 text-white text-[11.5px] font-semibold hover:bg-slate-800 disabled:opacity-50" data-testid="customer-edit-new-vehicle-save">
                    {vehicleSaving ? "Salvando…" : "Adicionar"}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : null}
        {editing && <CustomerDocumentsSection customerId={editing.id} />}

        {/* Fase 5 · Cérebro do Cliente (RAG) — só no modo edit */}
        {editing && (
          <CustomerBrainPanel
            customerId={editing.id}
            customerName={editing.name}
          />
        )}

        {/* Fase 6 · Anotação Profissional (Prontuário) — só no modo edit */}
        {editing && (
          <ClinicalNotesPanel
            customerId={editing.id}
            customerName={editing.name}
          />
        )}
      </form>
    </PremiumModal>
  );
}

// ─── Add-vehicle drawer ──────────────────────────────────────────────────
function AddVehicleDrawer({ customer, onClose, onSaved }) {
  const [form, setForm] = useState(emptyVehicle);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.identifier.trim()) { toast.error("Informe a placa."); return; }
    setSaving(true);
    try {
      await api.post("/vehicles", {
        customer_id: customer.id,
        identifier: form.identifier.trim().toUpperCase().replace(/[\s-]/g, ""),
        brand: form.brand || null,
        model: form.model || null,
        year: form.year ? parseInt(form.year, 10) : null,
        color: form.color || null,
        km: form.km ? parseInt(form.km, 10) : null,
        label: [form.brand, form.model].filter(Boolean).join(" ") || form.identifier,
      });
      toast.success("Veículo adicionado");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao adicionar veículo");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PremiumModal
      icon={Car}
      title="Novo veículo"
      subtitle={`Para ${customer.name}`}
      onClose={onClose}
      size="md"
      testid="add-vehicle-modal"
      footer={(
        <>
          <button type="button" onClick={onClose} className="text-[12.5px] font-medium text-slate-600 px-3 py-1.5 rounded-full hover:bg-slate-100">
            Cancelar
          </button>
          <button type="submit" form="add-vehicle-form" disabled={saving} data-testid="add-vehicle-submit" className="inline-flex items-center px-5 py-2 rounded-full bg-slate-900 text-white hover:bg-slate-800 transition-colors text-[12.5px] font-semibold shadow-sm shadow-slate-900/15 disabled:opacity-60">
            {saving ? "Salvando…" : "Adicionar veículo"}
          </button>
        </>
      )}
    >
      <form id="add-vehicle-form" onSubmit={submit} className="space-y-3.5">
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><FieldLabel>Placa *</FieldLabel><input autoFocus required value={form.identifier} onChange={(e) => setForm({ ...form, identifier: e.target.value })} placeholder="ABC1D23" data-testid="add-vehicle-plate" className={inputClsMono} /></label>
          <label className="block"><FieldLabel>Ano</FieldLabel><input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="2024" className={inputCls} /></label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><FieldLabel>Marca</FieldLabel><input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="BYD" data-testid="add-vehicle-brand" className={inputCls} /></label>
          <label className="block"><FieldLabel>Modelo</FieldLabel><input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="Song Pro" data-testid="add-vehicle-model" className={inputCls} /></label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><FieldLabel>Cor</FieldLabel><input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} placeholder="Preto" className={inputCls} /></label>
          <label className="block"><FieldLabel>KM atual</FieldLabel><input type="number" value={form.km} onChange={(e) => setForm({ ...form, km: e.target.value })} placeholder="32.000" className={inputCls} /></label>
        </div>
      </form>
    </PremiumModal>
  );
}

// ─── Export modal ────────────────────────────────────────────────────────
function ExportModal({ search, onClose }) {
  const [fmt, setFmt] = useState("xlsx");
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [yearFrom, setYearFrom] = useState("");
  const [yearTo, setYearTo] = useState("");
  const [includeVehicles, setIncludeVehicles] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const download = async (e) => {
    e.preventDefault();
    setDownloading(true);
    try {
      const params = { fmt, search: search || "", brand: brand.trim() || "", model: model.trim() || "", include_vehicles: includeVehicles };
      if (yearFrom) params.year_from = parseInt(yearFrom, 10);
      if (yearTo) params.year_to = parseInt(yearTo, 10);
      const res = await api.get("/customers/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fmt === "xlsx" ? "clientes.xlsx" : "clientes.csv";
      document.body.appendChild(a);
      a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Arquivo gerado — verifique seus downloads.");
      onClose();
    } catch (e) {
      toast.error("Falha ao exportar.");
    } finally { setDownloading(false); }
  };

  return (
    <PremiumModal
      icon={DownloadSimple}
      title="Exportar clientes"
      subtitle={search ? `Já com o filtro: "${search}"` : "Sem filtros — exporta todos."}
      onClose={onClose}
      size="md"
      testid="export-modal"
      footer={(
        <>
          <button type="button" onClick={onClose} className="text-[12.5px] font-medium text-slate-600 px-3 py-1.5 rounded-full hover:bg-slate-100">
            Cancelar
          </button>
          <button type="submit" form="export-form" disabled={downloading} data-testid="export-download-btn" className="inline-flex items-center px-5 py-2 rounded-full bg-slate-900 text-white hover:bg-slate-800 transition-colors text-[12.5px] font-semibold shadow-sm shadow-slate-900/15 disabled:opacity-60">
            {downloading ? "Gerando…" : "Baixar arquivo"}
          </button>
        </>
      )}
    >
      <form id="export-form" onSubmit={download} className="space-y-4">
        <div>
          <FieldLabel>Formato</FieldLabel>
          <div className="grid grid-cols-2 gap-2 mt-1.5">
            {["xlsx", "csv"].map((f) => (
              <button
                type="button" key={f} onClick={() => setFmt(f)}
                data-testid={`export-format-${f}`}
                className={`text-[13px] py-2.5 rounded-xl border transition-all ${
                  fmt === f
                    ? "border-slate-900 bg-slate-900 text-white font-semibold shadow-sm shadow-slate-900/15"
                    : "border-slate-200 bg-white hover:border-slate-400"
                }`}
              >
                {f === "xlsx" ? "Excel (.xlsx)" : "CSV (.csv)"}
              </button>
            ))}
          </div>
        </div>

        <details className="rounded-xl border border-slate-200 px-3.5 py-2.5 bg-slate-50/40" data-testid="export-vehicle-filters">
          <summary className="text-[13px] font-medium text-slate-800 cursor-pointer flex items-center gap-2 select-none">
            <Funnel size={13} weight="bold" /> Filtrar por veículo (opcional)
          </summary>
          <div className="mt-3 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><FieldLabel>Marca</FieldLabel><input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="BYD" data-testid="export-brand" className={inputCls} /></label>
              <label className="block"><FieldLabel>Modelo</FieldLabel><input value={model} onChange={(e) => setModel(e.target.value)} placeholder="Song Pro" data-testid="export-model" className={inputCls} /></label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><FieldLabel>Ano de</FieldLabel><input type="number" value={yearFrom} onChange={(e) => setYearFrom(e.target.value)} placeholder="2020" className={inputCls} /></label>
              <label className="block"><FieldLabel>até</FieldLabel><input type="number" value={yearTo} onChange={(e) => setYearTo(e.target.value)} placeholder="2024" className={inputCls} /></label>
            </div>
            <p className="text-[11.5px] text-slate-500 leading-snug">Útil pra uma campanha específica (ex: aditivo só pra um modelo).</p>
          </div>
        </details>

        <label className="flex items-start gap-2.5 cursor-pointer">
          <input type="checkbox" checked={includeVehicles} onChange={(e) => setIncludeVehicles(e.target.checked)} data-testid="export-include-vehicles" className="mt-0.5 w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900" />
          <span className="text-[13px] text-slate-700 leading-snug">
            Incluir colunas dos veículos
            <span className="block text-[11.5px] text-slate-500">placa, marca, modelo, ano, km</span>
          </span>
        </label>
      </form>
    </PremiumModal>
  );
}


// ══════════════════════════════════════════════════════════════════════
// Fase D · Documentos do cliente (repositório privado)
// Mostra tudo que o cliente enviou pelo WhatsApp OU o operador anexou.
// Grounded em `/api/customer-documents`.
// ══════════════════════════════════════════════════════════════════════

const DOC_TYPE_LABELS = {
  crlv: "CRLV / Documento veículo",
  rg: "RG",
  cnh: "CNH",
  cpf: "CPF",
  comprovante_residencia: "Comprovante residência",
  comprovante_renda: "Comprovante renda",
  ficha_cadastro: "Ficha (modelo)",
  ficha_preenchida: "Ficha preenchida",
  contrato_assinado: "Contrato assinado",
  orcamento_assinado: "Orçamento assinado",
  termo_garantia: "Termo garantia",
  nota_fiscal: "Nota fiscal",
  boleto: "Boleto",
  comprovante_pagamento: "Comprovante pagamento",
  foto_veiculo: "Foto veículo",
  foto_produto: "Foto produto",
  foto_servico: "Foto serviço",
  outro_documento: "Outro documento",
  unclassified: "Não classificado",
};

const SOURCE_LABELS = {
  customer_whatsapp: "Cliente (WhatsApp)",
  operator_upload: "Operador",
  ai_capture: "IA",
};

function _formatBytes(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function _formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function CustomerDocumentsSection({ customerId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all|verified|pending
  const [uploading, setUploading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/customer-documents?customer_id=${customerId}&limit=100`);
      setDocs(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setDocs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [customerId]);

  const filtered = useMemo(() => {
    if (filter === "verified") return docs.filter((d) => d.verified);
    if (filter === "pending") return docs.filter((d) => !d.verified);
    return docs;
  }, [docs, filter]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Arquivo muito grande — máx 20 MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("customer_id", customerId);
      await api.post("/customer-documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Documento anexado.");
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Falha ao enviar documento.";
      toast.error(msg);
    } finally {
      setUploading(false);
      e.target.value = ""; // permite re-upload do mesmo arquivo
    }
  };

  const handleVerify = async (doc) => {
    setBusyId(doc.id);
    try {
      await api.patch(`/customer-documents/${doc.id}`, { verified: !doc.verified });
      toast.success(doc.verified ? "Verificação removida." : "Documento verificado.");
      await load();
    } catch {
      toast.error("Falha ao atualizar.");
    } finally {
      setBusyId(null);
    }
  };

  const handleReclassify = async (doc) => {
    setBusyId(doc.id);
    try {
      const { data } = await api.post(`/customer-documents/${doc.id}/reclassify`);
      if (data?.success) {
        toast.success(`Reclassificado: ${DOC_TYPE_LABELS[data.doc_type] || data.doc_type}`);
      } else {
        toast(data?.reason === "feature_flag_off"
          ? "Classificador desligado (contate o admin)."
          : `Não foi possível reclassificar: ${data?.reason || "erro"}`, { icon: "⚠️" });
      }
      await load();
    } catch {
      toast.error("Falha ao reclassificar.");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Remover "${doc.original_filename}"? Fica registrado no audit.`)) return;
    setBusyId(doc.id);
    try {
      await api.delete(`/customer-documents/${doc.id}?reason=operator_delete`);
      toast.success("Documento removido.");
      await load();
    } catch {
      toast.error("Falha ao remover.");
    } finally {
      setBusyId(null);
    }
  };

  const openDownload = async (doc) => {
    // Baixa autenticado como blob e força download
    try {
      const resp = await api.get(`/customer-documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || "documento";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch {
      toast.error("Falha ao baixar.");
    }
  };

  const openInline = async (doc) => {
    try {
      const resp = await api.get(`/customer-documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error("Falha ao abrir.");
    }
  };

  const verifiedCount = docs.filter((d) => d.verified).length;
  const pendingCount = docs.length - verifiedCount;

  return (
    <div className="pt-4 border-t border-slate-100" data-testid="customer-documents-section">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 flex items-center gap-1.5">
          <Files size={12} weight="bold" />
          Documentos
          {docs.length > 0 && <span className="text-slate-400">· {docs.length}</span>}
        </div>
        <label className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-slate-700 hover:text-slate-900 px-2 py-1 rounded-full hover:bg-slate-100 transition-colors cursor-pointer" data-testid="customer-doc-upload-trigger">
          <Plus size={11} weight="bold" /> {uploading ? "Enviando…" : "Anexar"}
          <input type="file" className="hidden" onChange={handleUpload}
                 accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx,.xls,.xlsx,.txt,.csv"
                 disabled={uploading} data-testid="customer-doc-file-input" />
        </label>
      </div>

      {docs.length > 0 && (
        <div className="flex items-center gap-1.5 mb-3">
          {[
            { key: "all", label: `Todos (${docs.length})` },
            { key: "verified", label: `Verificados (${verifiedCount})` },
            { key: "pending", label: `Pendentes (${pendingCount})` },
          ].map((f) => (
            <button
              type="button"
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`customer-doc-filter-${f.key}`}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-full transition-colors ${
                filter === f.key
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-[12px] text-slate-400 py-3">Carregando documentos…</div>
      ) : filtered.length === 0 ? (
        <div className="text-[12.5px] text-slate-500 italic bg-slate-50 border border-slate-100 rounded-lg p-3" data-testid="customer-doc-empty">
          {docs.length === 0
            ? "Nenhum documento ainda. Quando o cliente enviar (ou você anexar), aparece aqui."
            : "Nenhum documento neste filtro."}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((doc) => {
            const isImage = doc.file_kind === "image";
            const isPdf = doc.file_kind === "pdf";
            const isExpanded = expandedId === doc.id;
            const extracted = doc.extracted_fields || {};
            const extractedKeys = Object.keys(extracted).filter(
              (k) => !["source_url", "safety_reason"].includes(k),
            );
            return (
              <div key={doc.id} className={`rounded-lg border ${doc.verified ? "border-emerald-200 bg-emerald-50/30" : "border-slate-200 bg-white"}`} data-testid={`customer-doc-item-${doc.id}`}>
                <div className="p-3">
                  <div className="flex items-start gap-2.5">
                    <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${isImage ? "bg-blue-50 text-blue-600" : isPdf ? "bg-rose-50 text-rose-600" : "bg-slate-100 text-slate-500"}`}>
                      {isImage ? <ImageIcon size={16} weight="regular" /> : isPdf ? <FilePdf size={16} weight="regular" /> : <FileText size={16} weight="regular" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[12.5px] font-semibold text-slate-900 truncate max-w-[220px]" title={doc.original_filename}>
                          {doc.original_filename}
                        </span>
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700" data-testid={`customer-doc-${doc.id}-type`}>
                          {DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type}
                        </span>
                        {doc.type_confidence > 0 && (
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${doc.type_confidence >= 80 ? "bg-emerald-100 text-emerald-800" : doc.type_confidence >= 50 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`} title={`Confiança da classificação IA`}>
                            {doc.type_confidence}%
                          </span>
                        )}
                        {doc.verified && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-600 text-white">
                            <CheckCircle size={9} weight="fill" /> Verificado
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-500">
                        <span>{SOURCE_LABELS[doc.source] || doc.source}</span>
                        <span>·</span>
                        <span>{_formatDate(doc.created_at)}</span>
                        <span>·</span>
                        <span>{_formatBytes(doc.size_bytes)}</span>
                      </div>
                    </div>
                    <div className="shrink-0 flex items-center gap-0.5">
                      <button type="button" onClick={() => openInline(doc)} title="Visualizar" data-testid={`customer-doc-${doc.id}-view`} className="p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100">
                        <Eye size={13} />
                      </button>
                      <button type="button" onClick={() => openDownload(doc)} title="Baixar" data-testid={`customer-doc-${doc.id}-download`} className="p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100">
                        <DownloadSimple size={13} />
                      </button>
                      <button type="button" onClick={() => handleVerify(doc)} disabled={busyId === doc.id}
                              title={doc.verified ? "Remover verificação" : "Marcar como verificado"}
                              data-testid={`customer-doc-${doc.id}-verify`}
                              className={`p-1.5 rounded-md ${doc.verified ? "text-emerald-600 hover:bg-emerald-50" : "text-slate-500 hover:text-emerald-700 hover:bg-emerald-50"} disabled:opacity-40`}>
                        <CheckCircle size={13} weight={doc.verified ? "fill" : "regular"} />
                      </button>
                      <button type="button" onClick={() => handleReclassify(doc)} disabled={busyId === doc.id}
                              title="Reclassificar via IA" data-testid={`customer-doc-${doc.id}-reclassify`}
                              className="p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-40">
                        <ArrowClockwise size={13} className={busyId === doc.id ? "animate-spin" : ""} />
                      </button>
                      <button type="button" onClick={() => handleDelete(doc)} disabled={busyId === doc.id}
                              title="Remover" data-testid={`customer-doc-${doc.id}-delete`}
                              className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-40">
                        <Trash size={13} />
                      </button>
                    </div>
                  </div>

                  {extractedKeys.length > 0 && (
                    <div className="mt-2.5">
                      <button type="button" onClick={() => setExpandedId(isExpanded ? null : doc.id)}
                              data-testid={`customer-doc-${doc.id}-toggle-fields`}
                              className="inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-900">
                        <Sparkle size={10} weight="fill" className="text-violet-500" />
                        Campos extraídos pela IA ({extractedKeys.length})
                        <CaretRight size={9} className={`transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                      </button>
                      {isExpanded && (
                        <div className="mt-2 grid grid-cols-1 gap-1 pl-3 border-l-2 border-violet-200" data-testid={`customer-doc-${doc.id}-fields-panel`}>
                          {extractedKeys.map((k) => {
                            const v = extracted[k];
                            const display = v === null || v === undefined || v === ""
                              ? <span className="italic text-slate-400">não legível</span>
                              : (typeof v === "object" ? JSON.stringify(v) : String(v));
                            return (
                              <div key={k} className="text-[11.5px]">
                                <span className="font-semibold text-slate-600">{k.replace(/_/g, " ")}: </span>
                                <span className="text-slate-800">{display}</span>
                              </div>
                            );
                          })}
                          {extracted.caption && (
                            <div className="text-[11px] text-slate-500 italic mt-1">
                              Legenda: &ldquo;{extracted.caption}&rdquo;
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
